// ═══════════════════════════════════════════════════════════════
// Operate.jsx — verticalstack Layer 2 Operate-A + Operate-B
// Path: src/components/Operate.jsx
//
// S7.B EDIT (May 21, 2026) — Wire RevPlanPanel trigger.
//   - Import RevPlanPanel from './RevPlanPanel'
//   - Add revPlanOpen state + handlers
//   - Add "📊 RevPlan" ghost button in section header
//   - Render <RevPlanPanel> at end of return, passing stage="operate"
//   No changes to existing tabs, queries, toasts, or data reads.
//   Other lifecycle stages will wire the trigger at Tier 3.
//
// S35 LAYER 2 OPERATE-B (Apr 28, 2026) — EDIT.
//   Extends S34 Operate-A from 2 to 4 top tabs by absorbing
//   /messages and /assets routes from PMApp.js. Path B dispatcher
//   pattern continues — Shell.jsx and PlaceholderDashboard.jsx
//   remain untouched (4th absorption with Shell pure).
//
//   AS-KK (HIGH reverse) — chose 4-tab extend over component split
//   to preserve Path B "one absorption = one component" pattern.
//   File grows from 1024 toward ~1700 lines. Acceptable; can split
//   in cleanup pass after S37 if it gets unwieldy.
//
//   AS-LL (LOW) — Assets tab attempts real read via
//   useWorkspaceCollection('assets'). Empty state expected pre-S11
//   seed; tab works automatically when S11 ships.
//
//   AS-MM (LOW) — Messages tab attempts real read via
//   useWorkspaceCollection('messageThreads'). Live schema is
//   nested clients/{cid}/messageThreads (AS-NN); the workspace-
//   scoped target query falls through to AS-W fallback and
//   renders empty/error per workspace.
//
//   AS-NN (LOW) — surfaced from MessageCenter.jsx audit:
//   live messageThreads collection is nested under clients,
//   not workspace-scoped at top level. Migration to top-level
//   workspaceId-scoped messageThreads + messages collections
//   deferred to Layer 2.5 (rules + CFs travel together with
//   AS-HH/JJ).
//
// S34 LAYER 2 OPERATE-A (Apr 28, 2026) — NEW FILE.
//
// PURPOSE
//   Real /operate render path. Replaces Layer 1 placeholder when
//   PlaceholderDashboard dispatches section === 'operate'.
//
// ARCHITECTURE (AS-CC Option B + AS-KK extend)
//   One Shell nav item ('Operate') with internal top tabs:
//   STR | LTR | Messages | Assets.
//   Shell.jsx remains untouched. Tabs never share a screen.
//
// TABS
//   STR tab      → read-only booking calendar list + click-to-detail
//                  Source: BookingCalendar.jsx + BookingDetail.jsx (orphans, AS-K)
//   LTR tab      → 6 sub-tabs: Applications | Leases | Deposits |
//                  Inspections | Rent | Showings
//                  Source: TenantApplication.jsx + LeaseExecution.jsx +
//                  LTR_Components.jsx (orphans, AS-K)
//                  Showings is placeholder per AS-EE
//   Messages tab → read-only thread list (S35 NEW, AS-MM)
//                  Source: MessageCenter.jsx (orphan, AS-K)
//                  Field shape: subject/participantName/participantRole/
//                  lastMessagePreview/lastMessageAt
//   Assets tab   → read-only asset register (S35 NEW, AS-LL)
//                  Source: AssetRegister.jsx (orphan, AS-K)
//                  Field shape from S11 spec: category/name/manufacturer/
//                  model/serialNumber/installDate/warrantyExpiry/
//                  expectedLifeYears/conditionScore/lastServiceDate/
//                  nextServiceDue
//
// DATA READS (AS-W fallback retry pattern)
//   bookings           where workspaceId == workspace.workspaceId
//   tenantApplications where workspaceId == workspace.workspaceId
//   leases             where workspaceId == workspace.workspaceId
//   securityDeposits   where workspaceId == workspace.workspaceId
//   inspections        where workspaceId == workspace.workspaceId
//   rentLedger         where workspaceId == workspace.workspaceId
//   messageThreads     where workspaceId == workspace.workspaceId  (S35, AS-NN)
//   assets             where workspaceId == workspace.workspaceId  (S35, AS-LL)
//   showings           NOT QUERIED — placeholder tab only (AS-EE)
//
// CF DEPENDENCIES (AS-Q + AS-DD stub strategy)
//   All write actions stub as 'Coming in Layer 2.5' toasts.
//   S35 adds 9 new toast stubs across Messages + Assets tabs:
//     - Send message, + New thread, Mark thread read, AI Assist (S15 AI-01)
//     - + New asset, Edit asset, Schedule service, Log service event,
//       AI Assist (asset condition + lifecycle)
//   Running total: ~22 toast stubs in Operate.jsx; ~27 across all
//   Layer 2 components.
//
// FORWARD-LOOKING (S36 Maintain — AS-PP + AS-QQ DEFERRED)
//   AS-PP: Messages tab will be duplicated into Maintain.jsx.
//          Same data; potentially filtered by participantRole
//          (vendor/sub on Maintain side, tenant/guest/owner on
//          Operate side). Decision finalized at S36 start.
//   AS-QQ: Assets tab will be duplicated into Maintain.jsx
//          alongside WorkOrder <-> Asset linkage (S11 adds
//          assetId on workOrders). Decision finalized at S36
//          start.
//
// VISUAL LANGUAGE (AS-V continuity, AS-X branding-deferred)
//   Inline hex literals matching prior Layer 2 components:
//     #003060 navy, #6B7280 muted, #9CA3AF label, #00B8D0 cyan accent.
//   No brand.js import. Same factGrid + card pattern.
//
// SCOPE NOTES
//   AS-FF: messageThreads + assets collections may be missing or
//          unseeded in vitality-services. Empty states render
//          correctly; AS-W fallback handles index-missing errors.
//   AS-AS: file ~1700 lines after S35. Kept inline to preserve
//          Path B "one absorption = one component file" pattern.
//          Split tabs into separate components in cleanup pass
//          after S37 if needed.
//   AS-GG: showings collection does not exist. Placeholder only.
//   AS-HH/JJ: rules audit deferred to Layer 2.5.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
// S7.B import — RevPlanPanel side panel
import RevPlanPanel from './RevPlanPanel';

// ── Inline styles (AS-V mirror of PlaceholderDashboard) ──────────────────

const styles = {
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
  },
  sectionHeader: {
    marginBottom: 24,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
  },
  sectionHeaderTitleBlock: {
    flex: '1 1 auto',
    minWidth: 240,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 4,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#6B7280',
    margin: 0,
  },
  // S7.B — RevPlan trigger button in header (ghost style, matches existing pattern)
  headerActions: {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
  },
  revPlanBtn: {
    padding: '10px 16px',
    background: '#FFFFFF',
    color: '#003060',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },

  // Top tab strip (STR | LTR | Messages | Assets)
  // S35: scrollable on mobile per S17 P3b pattern — 4 tabs may overflow at 375px
  topTabs: {
    display: 'flex',
    gap: 4,
    borderBottom: '1px solid #E5E7EB',
    marginBottom: 24,
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
  },
  topTab: {
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#6B7280',
    borderBottom: '2px solid transparent',
    fontFamily: 'Arial, sans-serif',
    marginBottom: -1,
    flexShrink: 0,
  },
  topTabActive: {
    color: '#003060',
    borderBottom: '2px solid #00B8D0',
  },

  // LTR sub-tab strip (6 tabs, scrollable on mobile per S17 P3b pattern)
  subTabs: {
    display: 'flex',
    gap: 4,
    padding: '0 0 0',
    borderBottom: '1px solid #E5E7EB',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    marginBottom: 24,
  },
  subTab: {
    padding: '8px 16px',
    background: 'none',
    border: 'none',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#6B7280',
    borderBottom: '2px solid transparent',
    fontFamily: 'Arial, sans-serif',
    flexShrink: 0,
    marginBottom: -1,
  },
  subTabActive: {
    color: '#003060',
    borderBottom: '2px solid #00B8D0',
  },

  // Cards (AS-V anchor)
  card: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 24,
    marginBottom: 20,
  },
  cardHeader: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    marginTop: 0,
    marginBottom: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardCount: {
    fontSize: 11,
    color: '#9CA3AF',
    fontWeight: 600,
  },

  // List rows (used across STR + LTR sub-tabs + Messages + Assets)
  listRow: {
    padding: '12px 16px',
    backgroundColor: '#F8F9FA',
    borderRadius: 6,
    border: '1px solid #E5E7EB',
    marginBottom: 8,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  listRowMain: {
    flex: 1,
    minWidth: 0,
  },
  listRowName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#003060',
    marginBottom: 2,
  },
  listRowMeta: {
    fontSize: 12,
    color: '#6B7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  listRowSide: {
    fontSize: 12,
    color: '#6B7280',
    flexShrink: 0,
    textAlign: 'right',
  },

  // Status pills
  statusPill: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 10,
    marginLeft: 8,
    flexShrink: 0,
  },

  // Action row + buttons
  actionRow: {
    display: 'flex',
    gap: 12,
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid #F3F4F6',
    flexWrap: 'wrap',
  },
  primaryBtn: {
    padding: '10px 16px',
    background: '#003060',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
  },
  ghostBtn: {
    padding: '10px 16px',
    background: '#FFFFFF',
    color: '#003060',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
  },

  // States
  loadingState: {
    color: '#9CA3AF',
    fontStyle: 'italic',
    fontSize: 14,
    padding: '8px 0',
  },
  errorState: {
    color: '#B91C1C',
    fontSize: 13,
    padding: '12px 16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: 6,
  },
  emptyState: {
    color: '#6B7280',
    fontSize: 14,
    fontStyle: 'italic',
    padding: '24px 8px',
    textAlign: 'center',
  },

  // Detail back-button row
  backRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    cursor: 'pointer',
    color: '#003060',
    fontSize: 13,
    fontWeight: 600,
  },

  // Showings placeholder
  showingsHero: {
    padding: 24,
    background: '#F8F9FA',
    border: '1px dashed #9CA3AF',
    borderRadius: 8,
    textAlign: 'center',
  },
  showingsHeroTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#003060',
    marginBottom: 8,
  },
  showingsHeroBody: {
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 1.6,
    maxWidth: 480,
    margin: '0 auto 16px',
  },
  showingsBadge: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1,
    padding: '3px 8px',
    borderRadius: 4,
    background: '#FEF3C7',
    color: '#92400E',
    marginBottom: 12,
  },

  // ── S35 Messages tab styles ──────────────────────────────────────────
  messageThreadRow: {
    padding: '12px 16px',
    backgroundColor: '#F8F9FA',
    borderRadius: 6,
    border: '1px solid #E5E7EB',
    marginBottom: 8,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  messageThreadHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
  },
  messageThreadSubject: {
    fontSize: 14,
    fontWeight: 700,
    color: '#003060',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  messageThreadDate: {
    fontSize: 11,
    color: '#9CA3AF',
    fontWeight: 600,
    flexShrink: 0,
  },
  messageThreadPreview: {
    fontSize: 12,
    color: '#6B7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.4,
  },
  messageThreadRole: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#00B8D0',
    marginTop: 2,
  },

  // ── S35 Assets tab styles ────────────────────────────────────────────
  assetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 12,
  },
  assetCard: {
    background: '#F8F9FA',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 16,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  assetCardHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  assetCategoryTag: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#003060',
    background: '#DBEAFE',
    padding: '3px 8px',
    borderRadius: 4,
  },
  assetName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
  },
  assetModelLine: {
    fontSize: 12,
    color: '#6B7280',
    margin: 0,
  },
  assetMetaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
    fontSize: 11,
    color: '#6B7280',
  },
  assetMetaLabel: {
    fontWeight: 700,
    color: '#9CA3AF',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontSize: 10,
  },
  assetMetaValue: {
    color: '#003060',
    fontWeight: 600,
  },
  lifecycleWrap: {
    marginTop: 6,
  },
  lifecycleHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 10,
    color: '#9CA3AF',
    marginBottom: 3,
  },
  lifecycleTrack: {
    height: 5,
    background: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
  },
  lifecycleFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s',
  },

  // Toast
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#003060',
    color: '#FFFFFF',
    padding: '10px 20px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    zIndex: 200,
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  },
};

// ── Helpers ──────────────────────────────────────────────────────────────

const CHANNEL_COLORS = {
  airbnb: '#FF5A5F',
  vrbo:   '#3D67FF',
  ical:   '#8B5CF6',
  direct: '#003060',
};

const BOOKING_STATUS_COLORS = {
  pending_waiver: { bg: '#FEF3C7', color: '#92400E' },
  waiver_signed:  { bg: '#DBEAFE', color: '#1E40AF' },
  active:         { bg: '#DCFCE7', color: '#166534' },
  checked_out:    { bg: '#F3F4F6', color: '#374151' },
  cancelled:      { bg: '#FEE2E2', color: '#991B1B' },
};

const APP_STATUS_COLORS = {
  pending:  { bg: '#FEF3C7', color: '#92400E' },
  approved: { bg: '#DCFCE7', color: '#166534' },
  denied:   { bg: '#FEE2E2', color: '#991B1B' },
};

const LEASE_STATUS_COLORS = {
  draft:      { bg: '#F3F4F6', color: '#374151' },
  active:     { bg: '#DCFCE7', color: '#166534' },
  expired:    { bg: '#FEF3C7', color: '#92400E' },
  terminated: { bg: '#FEE2E2', color: '#991B1B' },
};

const DEPOSIT_STATUS_COLORS = {
  held:                { bg: '#DBEAFE', color: '#1E40AF' },
  returned:            { bg: '#DCFCE7', color: '#166534' },
  partially_returned:  { bg: '#FEF3C7', color: '#92400E' },
};

// S35: condition tier config (mirrors AssetRegister.jsx orphan, AS-X inline hex)
const CONDITION_CONFIG = {
  excellent: { min: 9, label: 'Excellent', color: '#16a34a', bg: '#f0fdf4' },
  good:      { min: 7, label: 'Good',      color: '#2563eb', bg: '#eff6ff' },
  fair:      { min: 5, label: 'Fair',      color: '#d97706', bg: '#fffbeb' },
  poor:      { min: 3, label: 'Poor',      color: '#ea580c', bg: '#fff7ed' },
  critical:  { min: 1, label: 'Critical',  color: '#dc2626', bg: '#fef2f2' },
};

function getCondition(score) {
  if (score == null) return null;
  const s = parseInt(score);
  if (isNaN(s)) return null;
  if (s >= 9) return CONDITION_CONFIG.excellent;
  if (s >= 7) return CONDITION_CONFIG.good;
  if (s >= 5) return CONDITION_CONFIG.fair;
  if (s >= 3) return CONDITION_CONFIG.poor;
  return CONDITION_CONFIG.critical;
}

function fmtMoney(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  });
}

function fmtDate(d) {
  if (!d) return '—';
  const date = d?.toDate ? d.toDate() : new Date(d);
  if (isNaN(date)) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// S35: short relative date for thread list — "Mar 14" or "Today"
function fmtDateShort(d) {
  if (!d) return '';
  const date = d?.toDate ? d.toDate() : new Date(d);
  if (isNaN(date)) return '';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return 'Today';
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString('en-US',
    sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }
  );
}

// S35: ISO date string ("2024-06-15") → Date at noon local for stable display
function parseAssetDate(s) {
  if (!s) return null;
  if (typeof s === 'string') {
    const d = new Date(s + 'T12:00:00');
    return isNaN(d) ? null : d;
  }
  if (s?.toDate) return s.toDate();
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

function StatusPill({ status, colorMap }) {
  const c = colorMap[status] || { bg: '#F3F4F6', color: '#374151' };
  return (
    <span style={{ ...styles.statusPill, background: c.bg, color: c.color }}>
      {(status || '—').replace(/_/g, ' ')}
    </span>
  );
}

// ── Generic collection loader hook (AS-W fallback retry) ─────────────────

function useWorkspaceCollection(collectionName, workspaceId, orderField = null) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!workspaceId) {
        setItems([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const constraints = [
          where('workspaceId', '==', workspaceId),
        ];
        if (orderField) constraints.push(orderBy(orderField, 'desc'));

        const q = query(collection(db, collectionName), ...constraints);
        const snap = await getDocs(q);
        if (cancelled) return;
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        if (cancelled) return;
        console.warn(`[Operate] ${collectionName} query failed, retrying without orderBy:`, err.message);
        try {
          const q2 = query(
            collection(db, collectionName),
            where('workspaceId', '==', workspaceId)
          );
          const snap2 = await getDocs(q2);
          if (cancelled) return;
          setItems(snap2.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (err2) {
          if (cancelled) return;
          console.warn(`[Operate] ${collectionName} fallback also failed:`, err2.message);
          setError(err2.message || `Failed to load ${collectionName}`);
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [collectionName, workspaceId, orderField]);

  return { items, loading, error };
}

// ═════════════════════════════════════════════════════════════════════════
// STR VIEW
// ═════════════════════════════════════════════════════════════════════════

function STRView({ workspace, showToast }) {
  const { items: bookings, loading, error } = useWorkspaceCollection('bookings', workspace.workspaceId);
  const [selectedBooking, setSelectedBooking] = useState(null);

  if (selectedBooking) {
    return (
      <BookingDetailView
        booking={selectedBooking}
        onBack={() => setSelectedBooking(null)}
        showToast={showToast}
      />
    );
  }

  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>
        <span>Short-Term Rental Bookings</span>
        <span style={styles.cardCount}>
          {loading ? '…' : `${bookings.length} bookings`}
        </span>
      </h2>

      {loading && <div style={styles.loadingState}>Loading bookings…</div>}

      {!loading && error && (
        <div style={styles.errorState}>Could not load bookings: {error}</div>
      )}

      {!loading && !error && bookings.length === 0 && (
        <div style={styles.emptyState}>
          No bookings in this workspace yet.
        </div>
      )}

      {!loading && !error && bookings.length > 0 && (
        <>
          {bookings.map((b) => {
            const channelColor = CHANNEL_COLORS[b.channel] || CHANNEL_COLORS.direct;
            return (
              <div
                key={b.id}
                style={styles.listRow}
                onClick={() => setSelectedBooking(b)}
              >
                <div style={{
                  width: 4, height: 36, borderRadius: 2, background: channelColor, flexShrink: 0,
                }} />
                <div style={styles.listRowMain}>
                  <div style={styles.listRowName}>{b.guestName || 'Guest'}</div>
                  <div style={styles.listRowMeta}>
                    {fmtDate(b.checkIn)} → {fmtDate(b.checkOut)} · {(b.channel || 'direct').toUpperCase()}
                  </div>
                </div>
                <StatusPill status={b.status} colorMap={BOOKING_STATUS_COLORS} />
              </div>
            );
          })}
        </>
      )}

      <div style={styles.actionRow}>
        <button
          style={styles.primaryBtn}
          onClick={() => showToast('Create booking — coming in Layer 2.5')}
        >
          + New booking
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('iCal sync — coming in Layer 2.5')}
        >
          Sync iCal
        </button>
      </div>
    </section>
  );
}

function BookingDetailView({ booking, onBack, showToast }) {
  const ci = fmtDate(booking.checkIn);
  const co = fmtDate(booking.checkOut);
  const channelColor = CHANNEL_COLORS[booking.channel] || CHANNEL_COLORS.direct;

  return (
    <>
      <div style={styles.backRow} onClick={onBack}>
        <span>←</span>
        <span>Back to bookings</span>
      </div>

      <section style={styles.card}>
        <h2 style={styles.cardHeader}>
          <span>{(booking.channel || 'direct').toUpperCase()} BOOKING</span>
          <StatusPill status={booking.status} colorMap={BOOKING_STATUS_COLORS} />
        </h2>

        <div style={{
          height: 4, borderRadius: 2, background: channelColor, marginBottom: 16,
        }} />

        <DetailRow label="Guest"     value={booking.guestName} />
        <DetailRow label="Email"     value={booking.guestEmail} />
        <DetailRow label="Phone"     value={booking.guestPhone || '—'} />
        <DetailRow label="Check-in"  value={ci} />
        <DetailRow label="Check-out" value={co} />
        <DetailRow label="Unit"      value={booking.unitId || '—'} />
        <DetailRow label="Channel"   value={(booking.channel || 'direct').toUpperCase()} />
      </section>

      <section style={styles.card}>
        <h2 style={styles.cardHeader}><span>Waiver</span></h2>
        <DetailRow
          label="Status"
          value={booking.waiverSignedAt ? `Signed ${fmtDate(booking.waiverSignedAt)}` : 'Not yet signed'}
        />
      </section>

      <section style={styles.card}>
        <h2 style={styles.cardHeader}><span>Access code</span></h2>
        {booking.accessCode ? (
          <>
            <DetailRow label="Code"   value={booking.accessCode} />
            <DetailRow label="Issued" value={fmtDate(booking.accessCodeIssuedAt)} />
          </>
        ) : (
          <div style={styles.emptyState}>No access code issued yet.</div>
        )}

        <div style={styles.actionRow}>
          <button
            style={styles.primaryBtn}
            onClick={() => showToast('Issue access code — coming in Layer 2.5')}
            disabled={!booking.waiverSignedAt}
          >
            Issue access code
          </button>
        </div>
      </section>
    </>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid #F3F4F6',
      fontSize: 13,
    }}>
      <span style={{ color: '#6B7280', fontWeight: 600 }}>{label}</span>
      <span style={{ color: '#003060', fontWeight: 600 }}>{value || '—'}</span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// LTR VIEW (6 sub-tabs)
// ═════════════════════════════════════════════════════════════════════════

const LTR_SUB_TABS = [
  { id: 'applications', label: 'Applications' },
  { id: 'leases',       label: 'Leases' },
  { id: 'deposits',     label: 'Deposits' },
  { id: 'inspections',  label: 'Inspections' },
  { id: 'rent',         label: 'Rent' },
  { id: 'showings',     label: 'Showings' },
];

function LTRView({ workspace, showToast }) {
  const [subTab, setSubTab] = useState('applications');

  return (
    <>
      <div style={styles.subTabs}>
        {LTR_SUB_TABS.map((t) => (
          <button
            key={t.id}
            style={{
              ...styles.subTab,
              ...(subTab === t.id ? styles.subTabActive : {}),
            }}
            onClick={() => setSubTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'applications' && <ApplicationsTab workspace={workspace} showToast={showToast} />}
      {subTab === 'leases'       && <LeasesTab       workspace={workspace} showToast={showToast} />}
      {subTab === 'deposits'     && <DepositsTab     workspace={workspace} showToast={showToast} />}
      {subTab === 'inspections'  && <InspectionsTab  workspace={workspace} showToast={showToast} />}
      {subTab === 'rent'         && <RentTab         workspace={workspace} showToast={showToast} />}
      {subTab === 'showings'     && <ShowingsTab     showToast={showToast} />}
    </>
  );
}

// ── Applications sub-tab ────────────────────────────────────────────────

function ApplicationsTab({ workspace, showToast }) {
  const { items: apps, loading, error } = useWorkspaceCollection('tenantApplications', workspace.workspaceId);

  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>
        <span>Tenant Applications</span>
        <span style={styles.cardCount}>{loading ? '…' : `${apps.length} apps`}</span>
      </h2>

      {loading && <div style={styles.loadingState}>Loading applications…</div>}

      {!loading && error && (
        <div style={styles.errorState}>Could not load applications: {error}</div>
      )}

      {!loading && !error && apps.length === 0 && (
        <div style={styles.emptyState}>No tenant applications yet.</div>
      )}

      {!loading && !error && apps.length > 0 && apps.map((a) => {
        const ratio = a.monthlyIncomeCents && a.requestedRentCents
          ? (a.monthlyIncomeCents / a.requestedRentCents).toFixed(1)
          : null;
        return (
          <div key={a.id} style={styles.listRow}>
            <div style={styles.listRowMain}>
              <div style={styles.listRowName}>
                {a.firstName} {a.lastName}
              </div>
              <div style={styles.listRowMeta}>
                Income: {fmtMoney(a.monthlyIncomeCents)} · Rent: {fmtMoney(a.requestedRentCents)}
                {ratio && ` · Ratio: ${ratio}x`}
              </div>
            </div>
            <StatusPill status={a.status} colorMap={APP_STATUS_COLORS} />
          </div>
        );
      })}

      <div style={styles.actionRow}>
        <button
          style={styles.primaryBtn}
          onClick={() => showToast('New application — coming in Layer 2.5')}
        >
          + New application
        </button>
      </div>
    </section>
  );
}

// ── Leases sub-tab ──────────────────────────────────────────────────────

function LeasesTab({ workspace, showToast }) {
  const { items: leases, loading, error } = useWorkspaceCollection('leases', workspace.workspaceId);

  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>
        <span>Leases</span>
        <span style={styles.cardCount}>{loading ? '…' : `${leases.length} leases`}</span>
      </h2>

      {loading && <div style={styles.loadingState}>Loading leases…</div>}

      {!loading && error && (
        <div style={styles.errorState}>Could not load leases: {error}</div>
      )}

      {!loading && !error && leases.length === 0 && (
        <div style={styles.emptyState}>No leases yet.</div>
      )}

      {!loading && !error && leases.length > 0 && leases.map((l) => (
        <div key={l.id} style={styles.listRow}>
          <div style={styles.listRowMain}>
            <div style={styles.listRowName}>{l.tenantName || `Lease ${l.id.slice(-8)}`}</div>
            <div style={styles.listRowMeta}>
              {fmtDate(l.startDate)} – {fmtDate(l.endDate)} · {fmtMoney(l.monthlyRentCents)}/mo
            </div>
          </div>
          <StatusPill status={l.status} colorMap={LEASE_STATUS_COLORS} />
        </div>
      ))}

      <div style={styles.actionRow}>
        <button
          style={styles.primaryBtn}
          onClick={() => showToast('Draft lease — coming in Layer 2.5')}
        >
          + Draft lease
        </button>
      </div>
    </section>
  );
}

// ── Deposits sub-tab ────────────────────────────────────────────────────

function DepositsTab({ workspace, showToast }) {
  const { items: deposits, loading, error } = useWorkspaceCollection('securityDeposits', workspace.workspaceId);

  const heldTotal = useMemo(
    () => deposits
      .filter((d) => d.status === 'held')
      .reduce((s, d) => s + (d.amountCents || 0), 0),
    [deposits]
  );

  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>
        <span>Security Deposits</span>
        <span style={styles.cardCount}>
          {loading ? '…' : `${deposits.filter((d) => d.status === 'held').length} held · ${fmtMoney(heldTotal)} total`}
        </span>
      </h2>

      {loading && <div style={styles.loadingState}>Loading deposits…</div>}

      {!loading && error && (
        <div style={styles.errorState}>Could not load deposits: {error}</div>
      )}

      {!loading && !error && deposits.length === 0 && (
        <div style={styles.emptyState}>No security deposits yet.</div>
      )}

      {!loading && !error && deposits.length > 0 && deposits.map((d) => (
        <div key={d.id} style={styles.listRow}>
          <div style={styles.listRowMain}>
            <div style={styles.listRowName}>{d.tenantName || 'Tenant'}</div>
            <div style={styles.listRowMeta}>
              Lease {d.leaseId?.slice(-8)} · {fmtMoney(d.amountCents)}
            </div>
          </div>
          <StatusPill status={d.status} colorMap={DEPOSIT_STATUS_COLORS} />
        </div>
      ))}

      <div style={styles.actionRow}>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('Process return — coming in Layer 2.5')}
        >
          Process return
        </button>
      </div>
    </section>
  );
}

// ── Inspections sub-tab ─────────────────────────────────────────────────

function InspectionsTab({ workspace, showToast }) {
  const { items: inspections, loading, error } = useWorkspaceCollection('inspections', workspace.workspaceId);

  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>
        <span>Move-In Inspections</span>
        <span style={styles.cardCount}>{loading ? '…' : `${inspections.length} on file`}</span>
      </h2>

      {loading && <div style={styles.loadingState}>Loading inspections…</div>}

      {!loading && error && (
        <div style={styles.errorState}>Could not load inspections: {error}</div>
      )}

      {!loading && !error && inspections.length === 0 && (
        <div style={styles.emptyState}>No inspections recorded yet.</div>
      )}

      {!loading && !error && inspections.length > 0 && inspections.map((i) => (
        <div key={i.id} style={styles.listRow}>
          <div style={styles.listRowMain}>
            <div style={styles.listRowName}>
              {i.tenantName || 'Inspection'}
            </div>
            <div style={styles.listRowMeta}>
              {fmtDate(i.inspectionDate || i.createdAt)} · {i.unitId || i.propertyId || '—'}
            </div>
          </div>
          <span style={styles.listRowSide}>
            {i.status || '—'}
          </span>
        </div>
      ))}

      <div style={styles.actionRow}>
        <button
          style={styles.primaryBtn}
          onClick={() => showToast('New inspection — coming in Layer 2.5')}
        >
          + New inspection
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('AI inspection report — coming in Layer 2.5')}
        >
          Generate AI report
        </button>
      </div>
    </section>
  );
}

// ── Rent sub-tab ────────────────────────────────────────────────────────

function RentTab({ workspace, showToast }) {
  const { items: ledger, loading, error } = useWorkspaceCollection('rentLedger', workspace.workspaceId);

  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>
        <span>Rent Ledger</span>
        <span style={styles.cardCount}>{loading ? '…' : `${ledger.length} entries`}</span>
      </h2>

      {loading && <div style={styles.loadingState}>Loading rent ledger…</div>}

      {!loading && error && (
        <div style={styles.errorState}>Could not load rent ledger: {error}</div>
      )}

      {!loading && !error && ledger.length === 0 && (
        <div style={styles.emptyState}>No rent activity yet.</div>
      )}

      {!loading && !error && ledger.length > 0 && ledger.map((r) => (
        <div key={r.id} style={styles.listRow}>
          <div style={styles.listRowMain}>
            <div style={styles.listRowName}>
              {r.tenantName || `Lease ${r.leaseId?.slice(-8) || '—'}`}
            </div>
            <div style={styles.listRowMeta}>
              {fmtDate(r.dueDate || r.paidDate)} · {r.type || 'rent'}
            </div>
          </div>
          <span style={{
            ...styles.listRowSide,
            color: r.status === 'paid' ? '#166534' : r.status === 'overdue' ? '#991B1B' : '#6B7280',
            fontWeight: 700,
          }}>
            {fmtMoney(r.amountCents)}
          </span>
        </div>
      ))}

      <div style={styles.actionRow}>
        <button
          style={styles.primaryBtn}
          onClick={() => showToast('Record payment — coming in Layer 2.5')}
        >
          Record payment
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('Send late notice — coming in Layer 2.5')}
        >
          Send late notice
        </button>
      </div>
    </section>
  );
}

// ── Showings sub-tab (placeholder per AS-EE) ────────────────────────────

function ShowingsTab({ showToast }) {
  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>
        <span>Showings</span>
        <span style={styles.cardCount}>Placeholder</span>
      </h2>

      <div style={styles.showingsHero}>
        <div style={styles.showingsBadge}>COMING IN LAYER 2.5</div>
        <div style={styles.showingsHeroTitle}>
          Self-scheduled showings + door codes + feedback
        </div>
        <div style={styles.showingsHeroBody}>
          Prospective tenants will book showing slots from the public listing page.
          Owner or PM issues a one-time door code at the scheduled time. After the showing,
          the tenant submits feedback that flows back to the listing dashboard.
          UX slot reserved. Real implementation lands when the showings collection
          and supporting CFs ship in Layer 2.5.
        </div>
      </div>

      <div style={styles.actionRow}>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('Schedule showing — coming in Layer 2.5')}
        >
          Schedule showing
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('Issue door code — coming in Layer 2.5')}
        >
          Issue door code
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('Submit feedback — coming in Layer 2.5')}
        >
          Submit feedback
        </button>
      </div>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// MESSAGES VIEW (S35 — Layer 2 Operate-B, AS-MM)
// ═════════════════════════════════════════════════════════════════════════
//
// Read-only thread list. Field shape from MessageCenter.jsx orphan audit:
//   subject, participantName, participantRole, lastMessagePreview,
//   lastMessageAt, unreadCount.
//
// Live schema is nested clients/{cid}/messageThreads (AS-NN) — this query
// targets future workspace-scoped messageThreads. Falls through to AS-W
// fallback against current empty top-level collection. Empty state expected.
//
// Click-through to thread pane deferred to Layer 2.5 (AS-DD read-only).
// AI Assist (S15 AI-01 draftGuestComm) wired in Layer 2.5 alongside writes.

function MessagesView({ workspace, showToast }) {
  const { items: threads, loading, error } = useWorkspaceCollection(
    'messageThreads',
    workspace.workspaceId
  );

  // Sort newest-first by lastMessageAt (client-side; orderBy in hook would
  // require composite index — AS-W fallback strategy)
  const sortedThreads = useMemo(() => {
    return [...threads].sort((a, b) => {
      const ta = a.lastMessageAt?.toDate?.()?.getTime?.() ?? 0;
      const tb = b.lastMessageAt?.toDate?.()?.getTime?.() ?? 0;
      return tb - ta;
    });
  }, [threads]);

  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>
        <span>Conversations</span>
        <span style={styles.cardCount}>
          {loading ? '…' : `${threads.length} thread${threads.length === 1 ? '' : 's'}`}
        </span>
      </h2>

      {loading && <div style={styles.loadingState}>Loading conversations…</div>}

      {!loading && error && (
        <div style={styles.errorState}>Could not load conversations: {error}</div>
      )}

      {!loading && !error && threads.length === 0 && (
        <div style={styles.emptyState}>
          No conversations in this workspace yet.
        </div>
      )}

      {!loading && !error && sortedThreads.length > 0 && sortedThreads.map((t) => (
        <div
          key={t.id}
          style={styles.messageThreadRow}
          onClick={() => showToast('Open thread — coming in Layer 2.5')}
        >
          <div style={styles.messageThreadHeaderRow}>
            <div style={styles.messageThreadSubject}>
              {t.subject || t.participantName || 'Conversation'}
            </div>
            <div style={styles.messageThreadDate}>
              {fmtDateShort(t.lastMessageAt)}
            </div>
          </div>
          <div style={styles.messageThreadPreview}>
            {t.lastMessagePreview || '(no messages)'}
          </div>
          {t.participantRole && (
            <div style={styles.messageThreadRole}>
              {t.participantRole}
            </div>
          )}
        </div>
      ))}

      <div style={styles.actionRow}>
        <button
          style={styles.primaryBtn}
          onClick={() => showToast('New conversation — coming in Layer 2.5')}
        >
          + New thread
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('Mark all read — coming in Layer 2.5')}
        >
          Mark all read
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('AI Assist (draftGuestComm) — coming in Layer 2.5')}
        >
          ✨ AI Assist
        </button>
      </div>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ASSETS VIEW (S35 — Layer 2 Operate-B, AS-LL)
// ═════════════════════════════════════════════════════════════════════════
//
// Read-only asset register. Field shape from S11 spec (locked):
//   assetId, category, name, manufacturer, model, serialNumber,
//   installDate, warrantyExpiry, expectedLifeYears, conditionScore,
//   lastServiceDate, nextServiceDue, iotDeviceId, iotProvider,
//   parcelId, structureId, unitId.
//
// Pre-S11 the assets collection is unseeded; empty state expected
// across all workspaces. When S11 ships and seed uses workspaceId,
// this tab works without code changes (AS-LL).
//
// Lifecycle bar formula mirrors AssetRegister.jsx orphan: install +
// expectedLifeYears → % elapsed, color thresholds at 70%/90%.
// Condition tiers: 9-10 excellent, 7-8 good, 5-6 fair, 3-4 poor, 1-2 critical.
//
// Filter chips (category + condition) deferred to Layer 2.5 — keeping
// S35 to read-only flat grid per AS-DD.

function AssetsView({ workspace, showToast }) {
  const { items: assets, loading, error } = useWorkspaceCollection(
    'assets',
    workspace.workspaceId
  );

  // Group by category for the count summary
  const categoryCounts = useMemo(() => {
    const counts = {};
    assets.forEach((a) => {
      const c = a.category || 'Other';
      counts[c] = (counts[c] || 0) + 1;
    });
    return counts;
  }, [assets]);

  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>
        <span>Asset Register</span>
        <span style={styles.cardCount}>
          {loading
            ? '…'
            : `${assets.length} asset${assets.length === 1 ? '' : 's'}`}
        </span>
      </h2>

      {loading && <div style={styles.loadingState}>Loading assets…</div>}

      {!loading && error && (
        <div style={styles.errorState}>Could not load assets: {error}</div>
      )}

      {!loading && !error && assets.length === 0 && (
        <div style={styles.emptyState}>
          No assets recorded in this workspace yet.
          <div style={{ fontSize: 12, marginTop: 8, fontStyle: 'normal' }}>
            HVAC, plumbing, electrical, appliances, roofing, and more —
            tracked with condition scoring and lifecycle forecasting (S11).
          </div>
        </div>
      )}

      {!loading && !error && assets.length > 0 && (
        <>
          {Object.keys(categoryCounts).length > 0 && (
            <div style={{
              fontSize: 11,
              color: '#6B7280',
              marginBottom: 12,
            }}>
              {Object.entries(categoryCounts)
                .map(([cat, n]) => `${cat}: ${n}`)
                .join(' · ')}
            </div>
          )}

          <div style={styles.assetGrid}>
            {assets.map((a) => (
              <AssetCard
                key={a.id}
                asset={a}
                onClick={() => showToast('Edit asset — coming in Layer 2.5')}
              />
            ))}
          </div>
        </>
      )}

      <div style={styles.actionRow}>
        <button
          style={styles.primaryBtn}
          onClick={() => showToast('New asset — coming in Layer 2.5')}
        >
          + New asset
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('Schedule service — coming in Layer 2.5')}
        >
          Schedule service
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('Log service event — coming in Layer 2.5')}
        >
          Log service event
        </button>
        <button
          style={styles.ghostBtn}
          onClick={() => showToast('AI Assist (asset condition + lifecycle) — coming in Layer 2.5')}
        >
          ✨ AI Assist
        </button>
      </div>
    </section>
  );
}

function AssetCard({ asset, onClick }) {
  const condition = getCondition(asset.conditionScore);

  const installDate = parseAssetDate(asset.installDate);
  const expectedLife = asset.expectedLifeYears
    ? parseInt(asset.expectedLifeYears)
    : null;

  let lifecyclePct = null;
  let yearsLeft = null;
  let lifecycleColor = '#16a34a';
  if (installDate && expectedLife) {
    const end = new Date(installDate);
    end.setFullYear(end.getFullYear() + expectedLife);
    const total = end.getTime() - installDate.getTime();
    const elapsed = Date.now() - installDate.getTime();
    lifecyclePct = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
    yearsLeft = Math.max(0, ((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 365.25)));
    lifecycleColor = lifecyclePct >= 90 ? '#dc2626' : lifecyclePct >= 70 ? '#d97706' : '#16a34a';
  }

  const installLabel = installDate
    ? installDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : null;

  return (
    <div style={styles.assetCard} onClick={onClick}>
      <div style={styles.assetCardHeaderRow}>
        <div style={styles.assetCategoryTag}>{asset.category || 'Other'}</div>
        {condition && (
          <span style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '3px 9px',
            borderRadius: 10,
            background: condition.bg,
            color: condition.color,
          }}>
            {condition.label} ({asset.conditionScore}/10)
          </span>
        )}
      </div>

      <h3 style={styles.assetName}>{asset.name || 'Unnamed asset'}</h3>

      {(asset.manufacturer || asset.model) && (
        <p style={styles.assetModelLine}>
          {[asset.manufacturer, asset.model].filter(Boolean).join(' — ')}
        </p>
      )}

      <div style={styles.assetMetaGrid}>
        {asset.serialNumber && (
          <>
            <span style={styles.assetMetaLabel}>S/N</span>
            <span style={styles.assetMetaValue}>{asset.serialNumber}</span>
          </>
        )}
        {asset.warrantyExpiry && (
          <>
            <span style={styles.assetMetaLabel}>Warranty</span>
            <span style={styles.assetMetaValue}>
              {fmtDate(asset.warrantyExpiry)}
            </span>
          </>
        )}
        {asset.lastServiceDate && (
          <>
            <span style={styles.assetMetaLabel}>Last service</span>
            <span style={styles.assetMetaValue}>
              {fmtDate(asset.lastServiceDate)}
            </span>
          </>
        )}
        {asset.nextServiceDue && (
          <>
            <span style={styles.assetMetaLabel}>Next service</span>
            <span style={styles.assetMetaValue}>
              {fmtDate(asset.nextServiceDue)}
            </span>
          </>
        )}
      </div>

      {lifecyclePct != null && installLabel && (
        <div style={styles.lifecycleWrap}>
          <div style={styles.lifecycleHeaderRow}>
            <span>Installed {installLabel}</span>
            <span style={{ color: lifecycleColor, fontWeight: 700 }}>
              {lifecyclePct >= 100
                ? 'Past end of life'
                : `${yearsLeft.toFixed(1)}y remaining`}
            </span>
          </div>
          <div style={styles.lifecycleTrack}>
            <div style={{
              ...styles.lifecycleFill,
              width: `${lifecyclePct}%`,
              background: lifecycleColor,
            }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// MAIN OPERATE COMPONENT
// ═════════════════════════════════════════════════════════════════════════

export default function Operate({ workspace, roles, roleLabel }) {
  const [topTab, setTopTab] = useState('str');
  const [toast, setToast] = useState('');
  // S7.B — RevPlan side panel open/close state
  const [revPlanOpen, setRevPlanOpen] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2800);
  };

  if (!workspace) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading workspace…</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.sectionHeader}>
        <div style={styles.sectionHeaderTitleBlock}>
          <h1 style={styles.sectionTitle}>Operate</h1>
          <p style={styles.sectionDescription}>
            Day-to-day operations: bookings, leases, deposits, inspections, rent, showings, messages, assets.
          </p>
        </div>
        {/* S7.B — RevPlan trigger button (ghost style) */}
        <div style={styles.headerActions}>
          <button
            type="button"
            style={styles.revPlanBtn}
            onClick={() => setRevPlanOpen(true)}
            aria-label="Open RevPlan side panel"
          >
            <span>📊</span>
            <span>RevPlan</span>
          </button>
        </div>
      </header>

      {/* Top tabs: STR | LTR | Messages | Assets (S35 AS-KK) */}
      <div style={styles.topTabs}>
        <button
          style={{ ...styles.topTab, ...(topTab === 'str' ? styles.topTabActive : {}) }}
          onClick={() => setTopTab('str')}
        >
          STR
        </button>
        <button
          style={{ ...styles.topTab, ...(topTab === 'ltr' ? styles.topTabActive : {}) }}
          onClick={() => setTopTab('ltr')}
        >
          LTR
        </button>
        <button
          style={{ ...styles.topTab, ...(topTab === 'messages' ? styles.topTabActive : {}) }}
          onClick={() => setTopTab('messages')}
        >
          Messages
        </button>
        <button
          style={{ ...styles.topTab, ...(topTab === 'assets' ? styles.topTabActive : {}) }}
          onClick={() => setTopTab('assets')}
        >
          Assets
        </button>
      </div>

      {topTab === 'str'      && <STRView      workspace={workspace} showToast={showToast} />}
      {topTab === 'ltr'      && <LTRView      workspace={workspace} showToast={showToast} />}
      {topTab === 'messages' && <MessagesView workspace={workspace} showToast={showToast} />}
      {topTab === 'assets'   && <AssetsView   workspace={workspace} showToast={showToast} />}

      {toast && <div style={styles.toast}>{toast}</div>}

      {/* S7.B — RevPlan side panel (slide-in from right when open) */}
      <RevPlanPanel
        workspace={workspace}
        isOpen={revPlanOpen}
        onClose={() => setRevPlanOpen(false)}
        stage="operate"
      />
    </div>
  );
}
